import Layout from "../../components/Layout";
import { Helmet } from "react-helmet-async";
import {
  FiCreditCard,
  FiDollarSign,
  FiTrendingUp,
  FiPieChart,
  FiBarChart2,
  FiFileText,
  FiCalendar,
  FiSettings,
  FiActivity,
} from "react-icons/fi";

const ModuleFinance = () => {
  const trackCTA = () => {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "click", {
        event_category: "CTA",
        event_label: "Sign Up - Finance Page",
      });
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Finance | RedERP</title>
        <meta
          name="description"
          content="Track income, control expenses, manage budgets, and gain financial clarity with the Finance module in RedERP."
        />
      </Helmet>

      {/* Hero Block */}
      <section className="bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-10 relative">
          {/* Text */}
          <div className="flex-1 text-left mt-16">
            <h1 className="text-4xl font-bold mb-4">Finance Management</h1>
            <p className="mb-6 text-sm sm:text-base">
              Simplify your accounting operations, automate reporting, and get a real-time view of your cash flow and profitability.
            </p>
            <a
              href="https://app.rederp.pro/sign-up"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackCTA}
              className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
            >
              Get Started Now
            </a>
          </div>

          {/* Image */}
          <div className="flex-1 hidden md:flex justify-end mt-12 mb-0">
            <img
              src="/assets/images/finance1.png"
              alt="Finance Illustration"
              className="w-[420px] lg:w-[420px] xl:w-[420px] h-[310px] object-contain"
            />
          </div>
        </div>
      </section>

      {/* Feature List */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 text-left">
          {[
            {
              icon: <FiCreditCard size={24} />,
              title: "Expense Management",
              desc: "Record, categorize, and control expenses with approval workflows and budget limits.",
            },
            {
              icon: <FiDollarSign size={24} />,
              title: "Incomes & Profitability",
              desc: "Track earnings by department or project and visualize profit margins clearly.",
            },
            {
              icon: <FiPieChart size={24} />,
              title: "Chart of Accounts",
              desc: "Configure a custom account structure suitable for your organization's financial reporting.",
            },
            {
              icon: <FiTrendingUp size={24} />,
              title: "Budget Planning",
              desc: "Create and monitor monthly or annual budgets with variance tracking.",
            },
            {
              icon: <FiBarChart2 size={24} />,
              title: "Financial Reports",
              desc: "Auto-generate balance sheets, income statements, and cash flow reports.",
            },
            {
              icon: <FiFileText size={24} />,
              title: "General Ledger",
              desc: "Record every financial transaction in compliance with double-entry bookkeeping.",
            },
            {
              icon: <FiCalendar size={24} />,
              title: "Multi-Period Support",
              desc: "Close and reconcile periods, manage year-end adjustments and audits.",
            },
            {
              icon: <FiActivity size={24} />,
              title: "Cash Flow Tracking",
              desc: "Monitor receivables, payables, bank accounts, and payment schedules in one view.",
            },
            {
              icon: <FiSettings size={24} />,
              title: "Integration Ready",
              desc: "Connect seamlessly with Sales, Procurement, Payroll, and Projects for end-to-end finance flow.",
            },
          ].map((item, i) => (
            <div
              key={i}
              className="p-6 bg-gray-50 border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-3 text-primary mb-3">
                {item.icon}
                <h3 className="font-bold">{item.title}</h3>
              </div>
              <p className="text-sm text-gray-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[rgb(84,13,14)] to-black text-white text-center">
        <div className="max-w-xl mx-auto px-6">
          <h3 className="text-3xl font-bold mb-4">Gain Full Financial Visibility</h3>
          <p className="mb-6">Start your 7-day free trial and take control of your business finances with RedERP.</p>
          <a
            href="https://app.rederp.pro/sign-up"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-white text-primary font-semibold px-6 py-3 rounded-full hover:bg-gray-100"
          >
            Try Finance Module
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default ModuleFinance;
